import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { validatePresence } from 'ember-changeset-validations/validators';
import { BufferedChangeset } from 'ember-changeset/types';
import { task } from 'ember-concurrency';
import Config from 'volta/config/environment';

import EmberObject from '@ember/object';
import { later } from '@ember/runloop';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type BvFlashService from 'volta/services/bv-flash';
import type BvMetricsService from 'volta/services/bv-metrics';
import type StoreService from 'volta/services/store';
import type { IAjaxError } from 'volta/utils/error-utils';

import type SessionService from 'ember-simple-auth/services/session';
import type { TProvider } from 'volta/models/types/user';
const Authentication = EmberObject.extend({
  login: null,
  password: null
});

const Validations = {
  login: [validatePresence(true)],
  password: [validatePresence(true)]
};

interface IArgs {
  config: {
    tenantId: string;
    env: string;
    authorizedProviders: TProvider[];
  };
}

type AuthentificationChangeSet = BufferedChangeset & EmberObject;

export default class LoginForm extends Component<IArgs> {
  // Services
  // ~~~~~

  @service declare store: StoreService;
  @service declare session: SessionService;
  @service declare bvFlash: BvFlashService;
  @service declare bvMetrics: BvMetricsService;

  // Properties
  // ~~~~~

  @tracked authentication: any;
  @tracked authChangeset?: AuthentificationChangeSet;
  @tracked isError = false;

  // Computed Properties
  // ~~~~~

  get authorizedProviders() {
    if (!this.args || !this.args.config || !this.args.config.authorizedProviders) {
      return undefined;
    } else {
      return this.args.config.authorizedProviders.map((provider) => ({
        key: provider,
        url: `${Config.api.host}/${Config.api.namespace}/authenticate/${provider}`,
        icon: provider
      }));
    }
  }

  // Lifecycle Hooks
  // ~~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.authentication = Authentication.create();
    this.initChangeset(this.authentication);
  }

  // Actions
  // ~~~~~

  authenticate = task(async () => {
    if (!this.authChangeset) {
      return;
    }
    const { login, password } = this.authChangeset.getProperties('login', 'password');
    this.bvMetrics.trackAction(this, 'login', login as string);
    await this.authChangeset.validate();
    if (this.authChangeset.isValid) {
      this.authChangeset.execute();
      const data = {
        login,
        password,
        rememberMe: true
      };
      try {
        await this.session.authenticate('authenticator:jwt', data);
      } catch (error) {
        this._onAuthenticateError(error);
      }
    }
  });

  // Helper Function
  // ~~~~~

  initChangeset(auth: typeof Authentication) {
    this.authChangeset = Changeset(
      auth,
      lookupValidator(Validations),
      Validations
    ) as AuthentificationChangeSet;
  }

  // Private Callbacks
  // ~~~~~

  _onAuthenticateError(reason: IAjaxError) {
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    this.bvFlash.ajaxError(reason);
    this.session.store?.clear();
    this.isError = true;
    later(
      this,
      () => {
        this.isError = false;
      },
      1000
    );
  }
}
